// SDK › Signals › a rule (D114, APS-428; the mock's #/capture/:id, design/console-d/views/capture.js,
// as Mosaic mapped it onto spec 3872a74). The mock's canvas window and node style, with the spec's
// one rule shape fixed in it: a trigger (what the rule reads and up to 8 conditions that must all
// hold) joined to a keep (the families it releases, the window before it and what follows). No pan,
// zoom or palette: two nodes do not need them. Selecting a node opens its inspector beside the
// window. The page edits a draft of the rule and publishes the app's policy whole.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  appsQuery,
  instrumentationQuery,
  orgQuery,
  ruleStatsQuery,
  triggerSessionsQuery,
  useSaveInstrumentation,
} from '../api/queries'
import type { App, InstrumentationRule as Rule, RuleOp, SignalFamily } from '../api/types'
import { Confirm } from '../components/Confirm'
import { FAMILY_KEY } from '../components/DeviceData'
import {
  Btn,
  btnClass,
  EmptyNote,
  PageHead,
  ScrollX,
  Section,
  Seg,
  Status,
  useToast,
} from '../components/d'
import { SandboxReadOnly } from '../components/SandboxReadOnly'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { day, dayTime, duration, shortId } from '../lib/format'
import {
  conditionValue,
  emptyRule,
  familyTakesTriggers,
  fromTemplate,
  MAX_CONDITIONS,
  ON_KINDS,
  type OnKind,
  OPS,
  type RuleDraft,
  ruleErrors,
  STATUS_CLASSES,
  TEMPLATES,
  type TemplateId,
  toRuleDraft,
  withoutRule,
  withRule,
} from '../lib/rules'
import { type After, type Draft, FAMILIES, LIMITS, toBody, toDraft } from '../lib/signals'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import { Outcome } from './Sessions'
import type { ScopeSearch } from './search'
import { useWho } from './useWho'

export const ON_KEY = {
  network: 'rules.on.network',
  log: 'rules.on.log',
  log_event: 'rules.on.logEvent',
  span: 'rules.on.span',
  span_name: 'rules.on.spanName',
} as const satisfies Record<OnKind, MessageId>

export const OP_KEY = {
  eq: 'rules.op.eq',
  in: 'rules.op.in',
  prefix: 'rules.op.prefix',
  gte: 'rules.op.gte',
  lte: 'rules.op.lte',
  status_class: 'rules.op.statusClass',
  glob: 'rules.op.glob',
  exists: 'rules.op.exists',
} as const satisfies Record<RuleOp, MessageId>

export const TEMPLATE_KEY = {
  force_quit: 'rules.template.forceQuit',
  endpoint_5xx: 'rules.template.endpoint5xx',
  slow_screen: 'rules.template.slowScreen',
  declined: 'rules.template.declined',
  rage_taps: 'rules.template.rageTaps',
  offline: 'rules.template.offline',
} as const satisfies Record<TemplateId, MessageId>

const AFTER_KEY = {
  session: 'signals.after.session',
  window: 'signals.after.window',
} as const satisfies Record<After, MessageId>

/** The mock's node glyphs: a trigger's bolt and a keep's ring. */
export function RuleGlyph({ kind }: { kind: 'trigger' | 'keep' }) {
  return (
    <svg className="cr-gl" viewBox="0 0 16 16" aria-hidden="true">
      {kind === 'trigger' ? (
        <path d="M9 1.75 3.75 9h4l-1 5.25L12.25 7h-4z" />
      ) : (
        <>
          <circle cx="8" cy="8" r="5.75" />
          <circle cx="8" cy="8" r="2.25" fill="currentColor" />
        </>
      )}
    </svg>
  )
}

/** The page, or in the sandbox, which cannot change anything, the reason (D64). */
export function SignalsRulePage() {
  const { projectId } = useProjectParams()
  return sandboxFor(projectId) ? <SandboxReadOnly /> : <RuleView />
}

function RuleView() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const { ruleId } = useParams({ strict: false }) as { ruleId: string }
  const search = useSearch({ strict: false }) as ScopeSearch & { template?: string }
  const apps = useQuery(appsQuery(orgId, projectId))
  const org = useQuery(orgQuery(orgId))
  const list = apps.data ?? []
  const app = list.find((a) => a.id === search.app) ?? (list.length === 1 ? list[0] : undefined)
  const canEdit = org.data ? org.data.role === 'owner' || org.data.role === 'admin' : undefined
  const template = TEMPLATES.some((x) => x.id === search.template)
    ? (search.template as TemplateId)
    : undefined
  return (
    <main id="main" className="pg cr-pg">
      {apps.isPending ? (
        <>
          <PageHead title={t('rules.title')} />
          <Loading />
        </>
      ) : apps.isError ? (
        <>
          <PageHead title={t('rules.title')} />
          <ErrorState error={apps.error} retry={() => apps.refetch()} />
        </>
      ) : app ? (
        <RuleEditor
          key={`${app.id}/${ruleId}/${template ?? ''}`}
          app={app}
          ruleId={ruleId}
          template={template}
          canEdit={canEdit}
        />
      ) : (
        <>
          <PageHead title={t('rules.title')} />
          <EmptyNote title={t('rules.pickApp')}>
            <Link className="ul" to="/o/$orgId/p/$projectId/signals" params={{ orgId, projectId }}>
              {t('signals.title')}
            </Link>
          </EmptyNote>
        </>
      )}
    </main>
  )
}

type Node = 'trigger' | 'keep'

function RuleEditor({
  app,
  ruleId,
  template,
  canEdit: role,
}: {
  app: App
  ruleId: string
  template: TemplateId | undefined
  canEdit: boolean | undefined
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const canEdit = role === true
  const held = useQuery(instrumentationQuery(orgId, projectId, app.id))
  const save = useSaveInstrumentation(orgId, projectId, app.id)
  // Changed since it was read (409): reload before publishing again (D117).
  const stale = !!save.error && isApiError(save.error, 409)
  const who = useWho(orgId)
  const { toast, show } = useToast()
  const isNew = ruleId === 'new'
  const base: Draft | undefined = held.data ? toDraft(held.data) : undefined
  const existing: Rule | undefined = base?.rules.find((r) => r.id === ruleId)
  const [rule, setRule] = useState<RuleDraft>()
  const [node, setNode] = useState<Node>('trigger')
  // A policy from the server (a publish, another tab's) restarts the draft; the template's name is
  // worded once, so a change of language does not reset what was typed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: t only words a new template's name
  useEffect(() => {
    if (!held.data) return
    const d = toDraft(held.data)
    const r = d.rules.find((x) => x.id === ruleId)
    setRule(
      r
        ? toRuleDraft(r, d)
        : template
          ? fromTemplate(template, t(TEMPLATE_KEY[template]))
          : emptyRule(),
    )
  }, [held.data, ruleId, template])

  const scope = { app: app.id }
  const title = rule?.name.trim() || existing?.name || t(isNew ? 'rules.new' : 'rules.title')
  const crumb = (
    <Link to="/o/$orgId/p/$projectId/signals" params={{ orgId, projectId }} search={scope}>
      {t('signals.title')}
    </Link>
  )

  if (held.isPending || !base || !rule)
    return (
      <>
        <PageHead crumb={crumb} title={title} />
        {held.isError ? (
          <ErrorState error={held.error} retry={() => held.refetch()} />
        ) : (
          <Loading />
        )}
      </>
    )
  if (!isNew && !existing)
    return (
      <>
        <PageHead crumb={crumb} title={t('rules.title')} />
        <EmptyNote title={t('rules.missing')}>{t('rules.missingBody', { id: ruleId })}</EmptyNote>
      </>
    )

  const others = base.rules.filter((r) => r.id !== rule.id)
  const errors = ruleErrors(rule, others)
  const next = withRule(base, rule)
  // Changed against the rule as loaded, not the policy: a rule whose families hold different
  // windows reads as it is until someone edits it.
  const loaded = existing ? toRuleDraft(existing, base) : undefined
  const dirty = isNew || JSON.stringify(rule) !== JSON.stringify(loaded)
  const inUse = rule.families.some((f) => familyTakesTriggers(base, f))
  const set = (patch: Partial<RuleDraft>) => setRule({ ...rule, ...patch })

  function publish(e: FormEvent) {
    e.preventDefault()
    if (!canEdit || errors.length > 0 || !rule) return
    save.mutate(toBody(next), {
      onSuccess: () => {
        show(t('rules.published'))
        if (isNew) {
          // The id is the one withRule gave the new rule; the server keeps it.
          const id = next.rules[next.rules.length - 1]?.id
          if (id)
            void navigate({
              to: '/o/$orgId/p/$projectId/signals/rules/$ruleId',
              params: { orgId, projectId, ruleId: id },
              search: scope,
            })
        }
      },
    })
  }

  function remove() {
    if (!base || !existing) return
    save.mutate(toBody(withoutRule(base, existing.id)), {
      onSuccess: () =>
        void navigate({
          to: '/o/$orgId/p/$projectId/signals',
          params: { orgId, projectId },
          search: scope,
        }),
    })
  }

  const meta = isNew ? (
    <>
      <span className="cr-st">{`– ${t('rules.state.new')}`}</span>
      {` · ${appLine(app)}`}
    </>
  ) : (
    <>
      <span className="cr-st">
        {inUse ? `● ${t('rules.state.inUse')}` : `– ${t('rules.state.unused')}`}
      </span>
      {` · ${appLine(app)}`}
      {existing?.updated_at
        ? ` · ${(() => {
            // No stand-in for a missing author: the date alone.
            const by = who(existing.updated_by)
            const date = day(i18n, existing.updated_at)
            return by ? t('rules.edited', { who: by, date }) : t('rules.editedOn', { date })
          })()}`
        : null}
    </>
  )

  return (
    <form onSubmit={publish} aria-label={t('rules.title')}>
      <PageHead
        crumb={
          <>
            {crumb}
            {' / '}
            <span translate="no">{isNew ? t('rules.newCrumb') : rule.id}</span>
          </>
        }
        title={
          <input
            className="cr-title"
            aria-label={t('rules.name')}
            placeholder={t('rules.namePlaceholder')}
            maxLength={80}
            disabled={!canEdit}
            value={rule.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        }
        meta={meta}
        actions={
          <>
            {isNew ? (
              <Link
                className="btn ghost"
                to="/o/$orgId/p/$projectId/signals"
                params={{ orgId, projectId }}
                search={scope}
              >
                {t('signals.discard')}
              </Link>
            ) : (
              <>
                <Btn
                  variant="ghost"
                  disabled={!dirty || save.isPending}
                  onClick={() => existing && setRule(toRuleDraft(existing, base))}
                >
                  {t('signals.discard')}
                </Btn>
                {canEdit ? (
                  <Confirm
                    buttonClass="btn danger cr-del"
                    confirmClass="btn danger solid"
                    label={t('rules.delete')}
                    title={t('rules.deleteTitle', { name: existing?.name ?? rule.id })}
                    body={t('rules.deleteBody')}
                    confirm={t('rules.delete')}
                    busy={save.isPending}
                    onConfirm={remove}
                  />
                ) : null}
              </>
            )}
            <Btn
              type="submit"
              variant="solid"
              disabled={!canEdit || !dirty || errors.length > 0 || save.isPending || stale}
              title={stale ? t('signals.reloadFirst') : undefined}
            >
              {t('signals.publish')}
            </Btn>
          </>
        }
      />
      {role === false ? <p className="se-hint cr-note">{t('settings.adminOnly')}</p> : null}
      {errors.includes('limit') ? (
        <p role="alert" className="se-hint cr-note cr-err">
          {t('rules.limit')}
        </p>
      ) : null}
      {stale ? (
        <p role="alert" className="se-hint cr-note cr-stale">
          <Status s="miss" label={t('signals.staleWord')} /> <span>{t('signals.staleSave')}</span>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => {
              save.reset()
              void held.refetch()
            }}
          >
            {t('signals.reload')}
          </Btn>
        </p>
      ) : save.error ? (
        <p role="alert" className="se-hint cr-note cr-err">
          {isApiError(save.error, 403)
            ? t('settings.adminOnly')
            : isApiError(save.error, 400)
              ? t('signals.invalid')
              : t('state.failed')}
        </p>
      ) : null}
      <Section title={t('rules.flow')} meta={t('rules.flowSub')}>
        <div className="cr-win">
          <div className="cr-wbar">
            <b translate="no">{isNew ? t('rules.newCrumb') : rule.id}</b>
            <span>{dirty ? t('rules.draft') : t('rules.published')}</span>
          </div>
          <div className="cr-body">
            <div className="cr-vp">
              <svg className="cr-edges" aria-hidden="true">
                <path className="cr-e" d="M 216 112 L 296 112" />
                <path className="cr-arrh" d="M 296 108 L 304 112 L 296 116 z" />
              </svg>
              {/* biome-ignore lint/a11y/useSemanticElements: the two nodes of a rule, not a fieldset */}
              <div role="group" aria-label={t('rules.nodes')}>
                <button
                  type="button"
                  className="cr-n"
                  style={{ left: 48, top: 80 }}
                  aria-pressed={node === 'trigger'}
                  onClick={() => setNode('trigger')}
                >
                  <span className="cr-nk">
                    <RuleGlyph kind="trigger" />
                    {t('rules.node.trigger')}
                  </span>
                  <span className="cr-nl" translate="no">
                    {onLabel(t, rule)}
                  </span>
                  <span className="cr-ns">
                    {t('rules.conditions', { count: rule.where.length })}
                  </span>
                </button>
                <button
                  type="button"
                  className="cr-n act"
                  style={{ left: 304, top: 80 }}
                  aria-pressed={node === 'keep'}
                  onClick={() => setNode('keep')}
                >
                  <span className="cr-nk">
                    <RuleGlyph kind="keep" />
                    {t('rules.node.keep')}
                  </span>
                  <span className="cr-nl">{keepLabel(t, rule)}</span>
                  <span className="cr-ns">{familiesLabel(t, rule.families)}</span>
                </button>
              </div>
            </div>
            <aside className="cr-insp" aria-label={t('rules.inspector')}>
              {node === 'trigger' ? (
                <TriggerInspector rule={rule} canEdit={canEdit} errors={errors} set={set} />
              ) : (
                <KeepInspector rule={rule} draft={base} canEdit={canEdit} set={set} />
              )}
            </aside>
            <div className="cr-vl">
              <p className="cr-vn">{t('rules.larger')}</p>
              <ol>
                <li className="cr-vi">
                  <span className="cr-vg">
                    <RuleGlyph kind="trigger" />
                  </span>
                  <span>
                    <span className="cr-vk">{t('rules.node.trigger')}</span>
                    <b translate="no">{onLabel(t, rule)}</b>
                    {rule.where.map((c, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: conditions have no id
                      <span key={i} translate="no">
                        {conditionText(t, c)}
                      </span>
                    ))}
                  </span>
                </li>
                <li className="cr-vi act">
                  <span className="cr-vg">
                    <RuleGlyph kind="keep" />
                  </span>
                  <span>
                    <span className="cr-vk">{t('rules.node.keep')}</span>
                    <b>{keepLabel(t, rule)}</b>
                    <span>{familiesLabel(t, rule.families)}</span>
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </div>
        <p className="cr-one">{t('rules.oneStep')}</p>
      </Section>
      {isNew ? null : <RuleSessions app={app} ruleId={rule.id} />}
      {isNew ? (
        <Section title={t('rules.templates')} meta={t('rules.templatesSub')}>
          <ul className="rl cr-tpl cr-tpl2">
            {TEMPLATES.map((x) => (
              <li key={x.id}>
                <span>
                  <RuleFlow />
                  <span>
                    <b>{t(TEMPLATE_KEY[x.id])}</b>
                    <span className="cr-ts" translate="no">
                      {`${onLabel(t, fromTemplate(x.id, ''))} · ${x.where.map((c) => conditionText(t, c)).join(' · ')}`}
                    </span>
                  </span>
                </span>
                <Link
                  className={btnClass(undefined, 'sm')}
                  to="/o/$orgId/p/$projectId/signals/rules/$ruleId"
                  params={{ orgId, projectId, ruleId: 'new' }}
                  search={{ ...scope, template: x.id }}
                >
                  {t('rules.use')}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {toast}
    </form>
  )
}

/** The mock's flow preview for a one-step rule: the trigger, a line, the keep. */
export function RuleFlow() {
  return (
    <span className="cr-fp" aria-hidden="true">
      <i className="t">
        <RuleGlyph kind="trigger" />
      </i>
      <s />
      <i className="a">
        <RuleGlyph kind="keep" />
      </i>
    </span>
  )
}

type T = ReturnType<typeof useI18n>['t']

const appLine = (app: App) => app.name

/** What a trigger reads, as the node says it: the event or span name, or the kind. */
export function onLabel(t: T, r: Pick<RuleDraft, 'onKind' | 'onName'>): string {
  if ((r.onKind === 'log_event' || r.onKind === 'span_name') && r.onName.trim())
    return r.onName.trim()
  return t(ON_KEY[r.onKind])
}

/** One condition in words: attribute, operator, value. */
export function conditionText(t: T, c: { attr: string; op: RuleOp; value: string }): string {
  if (c.op === 'exists')
    return `${c.attr} ${t(c.value.trim() === 'false' ? 'rules.op.missing' : 'rules.op.exists')}`
  return `${c.attr} ${t(OP_KEY[c.op])} ${c.value}`
}

const keepLabel = (t: T, r: RuleDraft) =>
  t(r.after === 'window' ? 'rules.keep.window' : 'rules.keep.session', { n: r.windowMinutes })

function familiesLabel(t: T, families: readonly SignalFamily[]): string {
  if (families.length === 0) return t('rules.keep.none')
  return families.map((f) => t(FAMILY_KEY[f])).join(', ')
}

function TriggerInspector({
  rule,
  canEdit,
  errors,
  set,
}: {
  rule: RuleDraft
  canEdit: boolean
  errors: ReturnType<typeof ruleErrors>
  set: (patch: Partial<RuleDraft>) => void
}) {
  const { t } = useI18n()
  const onId = useId()
  const nameId = useId()
  const named = rule.onKind === 'log_event' || rule.onKind === 'span_name'
  const setCondition = (i: number, patch: Partial<RuleDraft['where'][number]>) =>
    set({ where: rule.where.map((c, j) => (j === i ? { ...c, ...patch } : c)) })
  const full = rule.where.length >= MAX_CONDITIONS
  return (
    <>
      <div className="cr-ih">
        <span className="cr-ik">
          <RuleGlyph kind="trigger" />
          {t('rules.node.trigger')}
        </span>
      </div>
      <div className="cr-ib">
        <label className="lbl" htmlFor={onId}>
          {t('rules.on')}
          <select
            id={onId}
            className="select"
            disabled={!canEdit}
            value={rule.onKind}
            onChange={(e) => set({ onKind: e.target.value as OnKind })}
          >
            {ON_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(ON_KEY[k])}
              </option>
            ))}
          </select>
        </label>
        {named ? (
          <label className="lbl" htmlFor={nameId}>
            {t(rule.onKind === 'log_event' ? 'rules.eventName' : 'rules.spanName')}
            <input
              id={nameId}
              className="input mono"
              translate="no"
              maxLength={128}
              disabled={!canEdit}
              placeholder={rule.onKind === 'log_event' ? 'app.screen.view' : 'app.screen.load'}
              aria-invalid={errors.includes('onName') || undefined}
              value={rule.onName}
              onChange={(e) => set({ onName: e.target.value })}
            />
          </label>
        ) : null}
        {/* biome-ignore lint/a11y/useSemanticElements: a list of conditions, not a form section */}
        <div className="cr-pp" role="group" aria-label={t('rules.where')}>
          <p className="cr-hint">{t('rules.allHold')}</p>
          <ul className="cr-conds">
            {rule.where.map((c, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: conditions have no id
              <li key={i}>
                <input
                  className="input mono"
                  aria-label={t('rules.attr', { n: i + 1 })}
                  placeholder="http.response.status_code"
                  translate="no"
                  maxLength={128}
                  disabled={!canEdit}
                  aria-invalid={
                    errors.includes(`condition:${i}`) && !c.attr.trim() ? true : undefined
                  }
                  value={c.attr}
                  onChange={(e) => setCondition(i, { attr: e.target.value })}
                />
                <select
                  className="select"
                  aria-label={t('rules.opOf', { n: i + 1 })}
                  disabled={!canEdit}
                  value={c.op}
                  onChange={(e) => {
                    const op = e.target.value as RuleOp
                    setCondition(i, {
                      op,
                      ...(op === 'status_class' ? { value: '5xx' } : {}),
                      ...(op === 'exists' ? { value: 'true' } : {}),
                    })
                  }}
                >
                  {OPS.map((op) => (
                    <option key={op} value={op}>
                      {t(OP_KEY[op])}
                    </option>
                  ))}
                </select>
                {c.op === 'status_class' ? (
                  <select
                    className="select"
                    aria-label={t('rules.valueOf', { n: i + 1 })}
                    disabled={!canEdit}
                    value={c.value}
                    onChange={(e) => setCondition(i, { value: e.target.value })}
                  >
                    {STATUS_CLASSES.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                ) : c.op === 'exists' ? (
                  <select
                    className="select"
                    aria-label={t('rules.valueOf', { n: i + 1 })}
                    disabled={!canEdit}
                    value={c.value === 'false' ? 'false' : 'true'}
                    onChange={(e) => setCondition(i, { value: e.target.value })}
                  >
                    <option value="true">{t('rules.op.present')}</option>
                    <option value="false">{t('rules.op.absent')}</option>
                  </select>
                ) : (
                  <input
                    className="input mono"
                    aria-label={t('rules.valueOf', { n: i + 1 })}
                    translate="no"
                    disabled={!canEdit}
                    placeholder={c.op === 'in' ? 'b, c' : c.op === 'glob' ? '*declined*' : ''}
                    aria-invalid={
                      c.value.trim() !== '' && conditionValue(c) === undefined ? true : undefined
                    }
                    value={c.value}
                    onChange={(e) => setCondition(i, { value: e.target.value })}
                  />
                )}
                <Btn
                  size="sm"
                  variant="ghost"
                  aria-label={t('rules.removeCondition', { n: i + 1 })}
                  disabled={!canEdit}
                  onClick={() => set({ where: rule.where.filter((_, j) => j !== i) })}
                >
                  ×
                </Btn>
              </li>
            ))}
          </ul>
          {rule.where.some((c) => c.op === 'glob') ? (
            <p className="cr-hint">{t('rules.globHint')}</p>
          ) : null}
          <Btn
            size="sm"
            disabled={!canEdit || full}
            onClick={() => set({ where: [...rule.where, { attr: '', op: 'eq', value: '' }] })}
          >
            {`+ ${t('rules.condition')}`}
          </Btn>
          {full ? <p className="cr-hint">{t('rules.conditionsMax')}</p> : null}
        </div>
      </div>
    </>
  )
}

function KeepInspector({
  rule,
  draft,
  canEdit,
  set,
}: {
  rule: RuleDraft
  draft: Draft
  canEdit: boolean
  set: (patch: Partial<RuleDraft>) => void
}) {
  const { t } = useI18n()
  const windowId = useId()
  const toggle = (f: SignalFamily) =>
    set({
      families: rule.families.includes(f)
        ? rule.families.filter((x) => x !== f)
        : FAMILIES.filter((x) => x === f || rule.families.includes(x)),
    })
  return (
    <>
      <div className="cr-ih">
        <span className="cr-ik">
          <RuleGlyph kind="keep" />
          {t('rules.node.keep')}
        </span>
      </div>
      <div className="cr-ib">
        <fieldset className="cr-fams">
          <legend>{t('rules.families')}</legend>
          {FAMILIES.filter((f) => f !== 'summary').map((f) => {
            const takes = familyTakesTriggers(draft, f)
            return (
              <label key={f} className="cr-fam">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={rule.families.includes(f)}
                  onChange={() => toggle(f)}
                />
                <span>{t(FAMILY_KEY[f])}</span>
                {takes ? null : (
                  <span className="cr-hint">
                    {t(
                      draft.families[f].mode === 'always'
                        ? 'rules.familyAlways'
                        : 'rules.familyNever',
                    )}
                  </span>
                )}
              </label>
            )
          })}
        </fieldset>
        <label className="lbl" htmlFor={windowId}>
          {t('signals.window')}
          <span className="cr-wm">
            <input
              id={windowId}
              className="input mono"
              type="number"
              inputMode="numeric"
              min={LIMITS.windowMinutes.min}
              max={LIMITS.windowMinutes.max}
              disabled={!canEdit}
              value={rule.windowMinutes}
              onChange={(e) => set({ windowMinutes: Number(e.target.value) })}
            />
            <span>{t('signals.minBefore')}</span>
          </span>
        </label>
        <Seg
          label={t('rules.after')}
          options={(['session', 'window'] as const).map((a) => ({
            value: a,
            label: t(AFTER_KEY[a]),
          }))}
          value={rule.after}
          onChange={(after) => set({ after })}
          disabled={!canEdit}
        />
        <p className="cr-hint">{t('rules.once')}</p>
      </div>
    </>
  )
}

const BUILTIN_WORD = {
  crash: 'signals.trigger.crash',
  error: 'signals.trigger.error',
  anr: 'signals.trigger.anr',
  rage_tap: 'signals.trigger.rageTap',
  dead_tap: 'signals.trigger.deadTap',
} as const satisfies Record<string, MessageId>

/** A trigger as people read it: a rule by its name, an app event by its name, a built-in by its word. */
export function triggerLabel(t: T, trigger: string, rules: readonly Rule[]): string {
  if (trigger.startsWith('rule:')) {
    const id = trigger.slice('rule:'.length)
    return rules.find((r) => r.id === id)?.name ?? id
  }
  if (trigger.startsWith('event:'))
    return `${t('signals.trigger.event')} ${trigger.slice('event:'.length)}`
  return trigger in BUILTIN_WORD ? t(BUILTIN_WORD[trigger as keyof typeof BUILTIN_WORD]) : trigger
}

/** The sessions this rule sent (D114, the mock's kept sessions): the 7-day count and the newest. */
function RuleSessions({ app, ruleId }: { app: App; ruleId: string }) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const stats = useQuery(ruleStatsQuery(projectId, app.id, ruleId))
  const sessions = useQuery(triggerSessionsQuery(projectId, app.id, `rule:${ruleId}`))
  const rows = sessions.data?.sessions ?? []
  return (
    <Section
      title={t('rules.sent')}
      meta={
        stats.data
          ? [
              t('rules.sentMeta', { count: stats.data.sessions }),
              stats.data.last_at
                ? t('rules.sentLast', { last: dayTime(i18n, stats.data.last_at) })
                : '',
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
    >
      {sessions.isPending ? (
        <Loading />
      ) : sessions.isError ? (
        <ErrorState error={sessions.error} retry={() => sessions.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyNote title={t('rules.sentNone')}>{t('rules.sentNoneBody')}</EmptyNote>
      ) : (
        <ScrollX label={t('table.of', { name: t('rules.sent') })}>
          <table className="t">
            <thead>
              <tr>
                <th scope="col">{t('rules.col.session')}</th>
                <th scope="col">{t('rules.col.started')}</th>
                <th scope="col" className="num">
                  {t('rules.col.length')}
                </th>
                <th scope="col">{t('rules.col.outcome')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.session_id}>
                  <td>
                    <Link
                      className="ul mono"
                      translate="no"
                      to="/o/$orgId/p/$projectId/sessions/$sessionId"
                      params={{ orgId, projectId, sessionId: x.session_id }}
                    >
                      {shortId(x.session_id)}
                    </Link>
                  </td>
                  <td>{dayTime(i18n, x.started_at)}</td>
                  <td className="num">{duration(i18n, x.duration_ms)}</td>
                  <td>
                    <Outcome outcome={x.outcome} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      )}
    </Section>
  )
}
